/**
 * Mirror of worker-api src/features/events/domain/eventSchedule.ts
 * Keep both files aligned when changing scheduling rules.
 */

export type FeaturedCarouselInput = {
  featuredOrder?: number | null;
  startDate: string;
  registrationDeadlineMinutes?: number | null;
  duration?: string | null;
};

export function getRegistrationCloseTimeMs(
  startDate: string,
  registrationDeadlineMinutes?: number | null
): number {
  return (
    new Date(startDate).getTime() - (registrationDeadlineMinutes ?? 0) * 60 * 1000
  );
}

export function getInscriptionCloseTimeMs(
  startDate: string,
  registrationDeadlineMinutes?: number | null,
  duration?: string | null
): number {
  if (registrationDeadlineMinutes === 0 && duration) {
    return computeEventEndDate(startDate, duration).getTime();
  }
  return getRegistrationCloseTimeMs(startDate, registrationDeadlineMinutes);
}

export function isFeaturedActiveInCarousel(
  input: FeaturedCarouselInput,
  nowMs: number = Date.now()
): boolean {
  if (input.featuredOrder == null) return false;
  return nowMs < getInscriptionCloseTimeMs(
    input.startDate,
    input.registrationDeadlineMinutes,
    input.duration
  );
}

export function computeEventEndDate(startDate: string, duration: string): Date {
  const eventDate = new Date(startDate);
  const durationText = String(duration ?? "").trim();

  if (!durationText) return eventDate;

  if (/^\d+$/.test(durationText)) {
    const minutes = Number(durationText);
    if (Number.isFinite(minutes)) {
      return new Date(eventDate.getTime() + minutes * 60 * 1000);
    }
    return eventDate;
  }

  const timeOnly = durationText.match(/^(\d{1,3}):([0-5]?\d)/);
  if (timeOnly) {
    eventDate.setHours(eventDate.getHours() + Number(timeOnly[1]));
    eventDate.setMinutes(eventDate.getMinutes() + Number(timeOnly[2] || 0));
    return eventDate;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(durationText) || durationText.includes("T")) {
    const durationDate = new Date(durationText);
    if (!Number.isNaN(durationDate.getTime())) {
      eventDate.setHours(eventDate.getHours() + durationDate.getHours());
      eventDate.setMinutes(eventDate.getMinutes() + durationDate.getMinutes());
    }
    return eventDate;
  }

  const fallbackMinutes = Number(durationText.match(/([0-9]+)/)?.[1] ?? 60);
  return new Date(eventDate.getTime() + fallbackMinutes * 60 * 1000);
}

export type EventOperationalStatus = "active" | "inactive" | "finished";

export function getEventOperationalStatus(
  active: boolean,
  startDate: string,
  duration: string,
  now: Date = new Date()
): EventOperationalStatus {
  if (now >= computeEventEndDate(startDate, duration)) return "finished";
  return active ? "active" : "inactive";
}
